import { isDefined, isNonEmpty } from '@sgme/fp';
import { TagOption } from 'components/shared/Tags/model';
import { useCallback } from 'react';
import { uniqueBy } from 'utils/core/array';

const sanitizePastedLabel = (label: string) => {
  const trimmed = label.trim();
  // "Equity" can arbitrarily be appended by the users to differentiate between equities and debt. Since SP makes no such difference, the suffix should be removed before the name is transmitted to the API.
  const equitySuffix = / equity$/i;
  return trimmed.replace(equitySuffix, '');
};
type Options<T> = {
  convertPastedLabelsToTags: (inputs: string[]) => Promise<readonly TagOption<T>[]>;
  tags: readonly TagOption<T>[];
} & (
  | {
      isMulti: true;
      onChanged: (newValue: readonly TagOption<T>[]) => void;
    }
  | {
      isMulti: false;
      onChanged: (newValue: TagOption<T> | undefined) => void;
    }
);
export function useFetchDetailsAndUpdateTags<T>({ convertPastedLabelsToTags, isMulti, onChanged, tags }: Options<T>) {
  return useCallback(
    async (value?: string) => {
      if (!isDefined(value)) {
        return;
      }
      const labelsToAdd = value
        .split(/,|;|"|\s{2,}|\t|\n|\r|\r\n/g)
        .filter(isNonEmpty)
        .map(label => sanitizePastedLabel(label))
        .filter(label => !!label && !tags.some(tag => tag.label === label));

      const allLabelsToSet = uniqueBy(labelsToAdd, item => item.toUpperCase());

      const convertedTags = await convertPastedLabelsToTags(allLabelsToSet);
      if (isMulti) {
        onChanged([...tags, ...convertedTags]);
      } else {
        onChanged(convertedTags.at(0));
      }
    },
    [convertPastedLabelsToTags, tags, isMulti, onChanged],
  );
}
